import React, { memo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { batch, useDispatch } from 'react-redux';
import { Button, ModalIsland } from '@saviynt/design-system';

import {
  A_KEY_DS,
  A_KEY_DT,
  A_KEY_EN,
  A_KEY_OS,
  A_KEY_RO,
  A_KEY_SA,
  A_KEY_SA_UN,
  ALERT_BANNER_DEFAULT,
  GLOBAL_ALERT_BANNER_TYPES,
} from '../../../../shared/constants';
import { globalActions } from '../../../../shared/store/globalSlice';
import {
  type ServiceAccountState,
  type ServiceIdentityActionManagerState,
  type ServiceIdSPItemSA,
} from '../../../../shared/types';
import { serviceIdentityActions } from '../../data-store/serviceIdentitySlice';
import {
  useDisableServiceId,
  useRotatePassword,
  useUpdateOwnerList,
  useUpdateServiceAccountList,
} from '../../hooks';

import LinkAccountAction from './LinkAccountAction';
import ManageOwnerAction from './ManageOwnerAction';

import './ServiceIdentityActionManager.css';

/**
 * Manages confirmation modals for service identity actions
 * (disable, delete, enable, rotate credential, link/unlink accounts). Executes the
 * selected action on confirm and displays success or error banners. Triggers a
 * row transition on delete to animate the item out of the list.
 * @param props - {@link ServiceIdentityActionManagerState}
 * @param props.isOpen - Whether the action manager is active.
 * @param props.id - The service identity ID to act on.
 * @param props.action - The action key (disable, delete-transition, enable, rotate, link, or unlink).
 * @param props.policyId - The password policy ID used for credential rotation.
 * @param props.targetServiceAccount - The specific service account targeted for unlinking.
 * @param props.currentServiceAccounts - Already-assigned service accounts for the link/unlink actions.
 * @returns Confirmation or link-account modal for the active action, or null when closed.
 */
function ServiceIdentityActionManager({
  isOpen,
  id,
  action,
  policyId,
  targetServiceAccount,
  currentServiceAccounts,
  currentOwners,
}: ServiceIdentityActionManagerState) {
  const intl = useIntl();
  const dispatch = useDispatch();
  // Initializations
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsModalOpen(true);
    }
  }, [dispatch, isOpen]);

  // Controller hook
  const { handleDisableAction, isActionLoading: isDActionLoading } =
    useDisableServiceId(id);
  const { handleRotatePassword, isRotatingPassword } = useRotatePassword(id);
  const { handleUpdateServiceAccountList, isSASaving } =
    useUpdateServiceAccountList(id);
  const { handleUpdateOwnerList, isMOSaving } = useUpdateOwnerList(id);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    dispatch(serviceIdentityActions.setActionManager(null));
  }, [dispatch]);

  /** Shared success/error handler for action API calls. */
  const handleActionResponse = useCallback(
    (responsePromise: Promise<unknown>, successFn: () => void = () => {}) => {
      responsePromise
        .then((response) => {
          const typedResponse = response as {
            success: boolean;
            msg: string;
          };

          if (typedResponse.success) {
            setIsModalOpen(false);

            batch(() => {
              dispatch(serviceIdentityActions.setActionManager(null));
              dispatch(
                globalActions.setGlobalAlertBanner({
                  ...ALERT_BANNER_DEFAULT,
                  kind: GLOBAL_ALERT_BANNER_TYPES.SUCCESS,
                  title: intl.formatMessage({
                    id: 'yg2HFT',
                    defaultMessage: 'Success',
                    description: 'Success',
                  }),
                  description: typedResponse.msg,
                  isOpen: true,
                })
              );
            });

            successFn();

            return;
          }

          throw Error('Action failed');
        })
        .catch(() => {
          dispatch(
            globalActions.setGlobalAlertBanner({
              ...ALERT_BANNER_DEFAULT,
              kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
              title: intl.formatMessage({
                id: 'KFYqlI',
                defaultMessage: 'Failed',
                description: 'Failed',
              }),
              description: intl.formatMessage({
                id: 'mqZtBh',
                defaultMessage:
                  'Not able to take action at the moment! Please try again later.',
                description:
                  'Not able to take action at the moment! Please try again later.',
              }),
              isOpen: true,
            })
          );
        });
    },
    [dispatch, intl]
  );

  const handleSubmit = useCallback(
    (
      {
        isDelete,
        isEnabled,
        policyId: passwordPolicyId,
        addAccounts,
        removeAccounts,
        finalList,
        ownersList,
      }: {
        isDelete?: boolean;
        isEnabled?: boolean;
        policyId?: number;
        addAccounts?: ServiceAccountState[];
        removeAccounts?: ServiceIdSPItemSA[];
        finalList?: ServiceAccountState[] | ServiceIdSPItemSA[];
        ownersList?: {
          name: string;
          id: number;
          rank: number;
          type: string;
          firstname: string | null;
          lastname: string | null;
        }[];
      } = {
        isDelete: false,
      }
    ) => {
      if (action === A_KEY_DS || action === A_KEY_DT || action === A_KEY_EN) {
        handleActionResponse(handleDisableAction(isDelete, isEnabled), () => {
          if (id !== undefined && action === A_KEY_EN) {
            dispatch(
              serviceIdentityActions.setRowsToUpdate({
                [id]: { status: 'enabled' },
              })
            );
          }

          if (id !== undefined && action === A_KEY_DS) {
            dispatch(
              serviceIdentityActions.setRowsToUpdate({
                [id]: { status: 'disabled' },
              })
            );
          }

          if (id !== undefined && action === A_KEY_DT) {
            dispatch(
              serviceIdentityActions.setRowsToUpdate({
                [id]: { status: 'deleted' },
              })
            );
          }
        });
      }

      if (action === A_KEY_RO && typeof passwordPolicyId === 'number') {
        handleActionResponse(handleRotatePassword(passwordPolicyId));
      }

      if (action === A_KEY_SA) {
        handleActionResponse(
          handleUpdateServiceAccountList({
            addAccounts:
              addAccounts?.map((r) => ({ accountKey: r.accountKey })) ??
              undefined,
            removeAccounts:
              removeAccounts?.map((r) => ({ accountKey: r.id })) ?? undefined,
          }),
          () => {
            if (id !== undefined) {
              dispatch(
                serviceIdentityActions.setRowsToUpdate({
                  [id]: {
                    serviceAccounts: (finalList ?? []) as ServiceAccountState[],
                  },
                })
              );
            }

            handleModalClose();
          }
        );
      }

      if (action === A_KEY_SA_UN) {
        handleActionResponse(
          handleUpdateServiceAccountList({
            removeAccounts:
              removeAccounts?.map((r) => ({ accountKey: r.id })) ?? undefined,
          }),
          () => {
            if (id !== undefined) {
              const typedFinalList = finalList as
                | ServiceIdSPItemSA[]
                | undefined;

              dispatch(
                serviceIdentityActions.setRowsToUpdate({
                  [id]: {
                    serviceAccounts: (typedFinalList?.map((r) => ({
                      accountKey: r.id,
                      accountName: r.name,
                      endpointName: r.endpoint,
                      endpointLogo: r.icon,
                      status: r.status,
                    })) ?? []) as ServiceAccountState[],
                  },
                })
              );
            }
          }
        );
      }

      if (action === A_KEY_OS) {
        handleActionResponse(handleUpdateOwnerList(ownersList ?? []), () => {
          if (id !== undefined) {
            dispatch(
              serviceIdentityActions.setRowsToUpdate({
                [id]: {
                  owners:
                    ownersList?.map((o) => ({
                      id: o.id,
                      name: o.name,
                      rank: o.rank,
                      status: 'active',
                      type: o.type,
                      firstname: o.firstname,
                      lastname: o.lastname,
                    })) ?? [],
                },
              })
            );
          }

          handleModalClose();
        });
      }
    },
    [
      id,
      action,
      dispatch,
      handleActionResponse,
      handleDisableAction,
      handleRotatePassword,
      handleUpdateServiceAccountList,
      handleUpdateOwnerList,
      handleModalClose,
    ]
  );

  // Render body
  const renderModals = useCallback(() => {
    if (action === A_KEY_DS) {
      return (
        <ModalIsland
          isOpen={isModalOpen}
          kind='warning'
          alertTitle={intl.formatMessage({
            id: 'Lxb/K2',
            defaultMessage:
              'Are you sure you want to disable this Service Identity?',
            description:
              'Are you sure you want to disable this Service Identity?',
          })}
          alertSubtitle={intl.formatMessage({
            id: 'ZsZLmk',
            defaultMessage:
              'Disabling this Service Identity will initiate requests to disable the Service Accounts underneath.',
            description:
              'Disabling this Service Identity will initiate requests to disable the Service Accounts underneath.',
          })}
          isOutsideClickEnabled={false}
          isCloseButtonVisible={!isDActionLoading}
          isCloseOnEscapeKeyEnabled={!isDActionLoading}
          onClose={handleModalClose}
          primaryButton={
            <Button
              type='button'
              kind='outlined'
              size='medium'
              isDisabled={isDActionLoading}
              isLoading={isDActionLoading}
              loaderText={intl.formatMessage({
                id: 'PCpuN9',
                defaultMessage: 'Disabling...',
                description: 'Disabling',
              })}
              onClick={handleSubmit}>
              <FormattedMessage
                id='ZlU3pl'
                defaultMessage='Yes, Disable'
                description='Confirmation button text for disabling a Service Identity'
              />
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={handleModalClose}
              isDisabled={isDActionLoading}>
              <FormattedMessage
                id='6PdOcy'
                defaultMessage='Cancel'
                description='Cancel'
              />
            </Button>
          }
        />
      );
    }

    if (action === A_KEY_DT) {
      return (
        <ModalIsland
          isOpen={isModalOpen}
          kind='warning'
          alertTitle={intl.formatMessage({
            id: 'RTyeCm',
            defaultMessage:
              'Are you sure you want to delete this Service Identity?',
            description:
              'Are you sure you want to delete this Service Identity?',
          })}
          alertSubtitle={intl.formatMessage({
            id: '7I0aqZ',
            defaultMessage:
              'Deleting this Service Identity will initiate requests to remove the Service Accounts underneath. This action is irreversible, and the Service Identity cannot be made active again.',
            description:
              'Deleting this Service Identity will initiate requests to remove the Service Accounts underneath. This action is irreversible, and the Service Identity cannot be made active again.',
          })}
          isOutsideClickEnabled={false}
          isCloseButtonVisible={!isDActionLoading}
          isCloseOnEscapeKeyEnabled={!isDActionLoading}
          onClose={handleModalClose}
          primaryButton={
            <Button
              type='button'
              kind='outlined'
              size='medium'
              isDisabled={isDActionLoading}
              isLoading={isDActionLoading}
              loaderText={intl.formatMessage({
                id: 'Vo7YCE',
                defaultMessage: 'Deleting...',
                description: 'Deleting',
              })}
              onClick={() => handleSubmit({ isDelete: true })}>
              <FormattedMessage
                id='/CAals'
                defaultMessage='Yes, Delete'
                description='Yes, Delete button for action'
              />
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={handleModalClose}
              isDisabled={isDActionLoading}>
              <FormattedMessage
                id='6PdOcy'
                defaultMessage='Cancel'
                description='Cancel'
              />
            </Button>
          }
        />
      );
    }

    if (action === A_KEY_EN) {
      return (
        <ModalIsland
          isOpen={isModalOpen}
          kind='warning'
          alertTitle={intl.formatMessage({
            id: 'q5VMMh',
            defaultMessage:
              'Are you sure you want to enable this Service Identity?',
            description:
              'Are you sure you want to enable this Service Identity?',
          })}
          alertSubtitle={intl.formatMessage({
            id: 'KQif46',
            defaultMessage:
              'Enabling this Service Identity will initiate requests to enable the Service Accounts underneath.',
            description:
              'Enabling this Service Identity will initiate requests to enable the Service Accounts underneath.',
          })}
          isOutsideClickEnabled={false}
          isCloseButtonVisible={!isDActionLoading}
          isCloseOnEscapeKeyEnabled={!isDActionLoading}
          onClose={handleModalClose}
          primaryButton={
            <Button
              type='button'
              kind='outlined'
              size='medium'
              isDisabled={isDActionLoading}
              isLoading={isDActionLoading}
              loaderText={intl.formatMessage({
                id: 'T32y8e',
                defaultMessage: 'Enabling...',
                description: 'Enabling',
              })}
              onClick={() => handleSubmit({ isEnabled: true })}>
              <FormattedMessage
                id='XvnbqW'
                defaultMessage='Yes, Enable'
                description='Yes, Enable button for action'
              />
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={handleModalClose}
              isDisabled={isDActionLoading}>
              <FormattedMessage
                id='6PdOcy'
                defaultMessage='Cancel'
                description='Cancel'
              />
            </Button>
          }
        />
      );
    }

    if (action === A_KEY_RO && typeof policyId === 'number') {
      if (currentServiceAccounts?.length === 0) {
        batch(() => {
          dispatch(
            globalActions.setGlobalAlertBanner({
              ...ALERT_BANNER_DEFAULT,
              kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
              title: '',
              description: intl.formatMessage({
                id: 'QNn9pu',
                defaultMessage:
                  'Currently no service accounts are linked to this service identity. Please link at least one service account before rotating the password.',
                description:
                  'Currently no service accounts are linked to this service identity. Please link at least one service account before rotating the password.',
              }),
              isOpen: true,
            })
          );
          dispatch(serviceIdentityActions.setActionManager(null));
        });

        return <div />;
      }

      return (
        <ModalIsland
          isOpen={isModalOpen}
          kind='warning'
          alertTitle={intl.formatMessage({
            id: 'camkJO',
            defaultMessage:
              'Are you sure you want to rotate the password for this Service Identity?',
            description:
              'Are you sure you want to rotate the password for this Service Identity?',
          })}
          alertSubtitle={intl.formatMessage({
            id: '+SNYik',
            defaultMessage:
              'This will initiate task creation to change the passwords of the Service Accounts underneath. The password policy at the Security System will take precedence over the Service Identity password policy.',
            description:
              'This will initiate task creation to change the passwords of the Service Accounts underneath. The password policy at the Security System will take precedence over the Service Identity password policy.',
          })}
          isOutsideClickEnabled={false}
          isCloseButtonVisible={!isRotatingPassword}
          isCloseOnEscapeKeyEnabled={!isRotatingPassword}
          onClose={handleModalClose}
          primaryButton={
            <Button
              type='button'
              kind='outlined'
              size='medium'
              isDisabled={isRotatingPassword}
              isLoading={isRotatingPassword}
              loaderText={intl.formatMessage({
                id: 'NiNY1+',
                defaultMessage: 'Rotating...',
                description: 'Rotating',
              })}
              onClick={() => handleSubmit({ policyId })}>
              <FormattedMessage
                id='oFQSO1'
                defaultMessage='Yes, Rotate'
                description='Yes, Rotate button for action'
              />
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={handleModalClose}
              isDisabled={isRotatingPassword}>
              <FormattedMessage
                id='6PdOcy'
                defaultMessage='Cancel'
                description='Cancel'
              />
            </Button>
          }
        />
      );
    }

    if (action === A_KEY_SA) {
      return (
        <LinkAccountAction
          serviceId={id}
          isModalOpen={isModalOpen}
          handleModalClose={handleModalClose}
          handleSubmit={handleSubmit}
          isLoading={isSASaving}
          currentServiceAccounts={currentServiceAccounts ?? []}
        />
      );
    }

    if (action === A_KEY_SA_UN) {
      return (
        <ModalIsland
          isOpen={isModalOpen}
          kind='warning'
          alertTitle={intl.formatMessage({
            id: 'YNuwQf',
            defaultMessage:
              'Are you sure you want to unlink this Service Account from the Service Identity?',
            description:
              'Are you sure you want to unlink this Service Account from the Service Identity?',
          })}
          alertSubtitle={intl.formatMessage({
            id: 'aXU9Ln',
            defaultMessage:
              'This will remove the Service Account from the Service Identity and it will no longer be included in operations initiated by this Service Identity.',
            description:
              'This will remove the Service Account from the Service Identity and it will no longer be included in operations initiated by this Service Identity.',
          })}
          isOutsideClickEnabled={false}
          isCloseButtonVisible={!isSASaving}
          isCloseOnEscapeKeyEnabled={!isSASaving}
          onClose={handleModalClose}
          primaryButton={
            <Button
              type='button'
              kind='outlined'
              size='medium'
              isDisabled={isSASaving}
              isLoading={isSASaving}
              loaderText={intl.formatMessage({
                id: 'TSXt48',
                defaultMessage: 'Unlinking...',
                description: 'Unlinking',
              })}
              onClick={() => {
                handleSubmit({
                  removeAccounts: targetServiceAccount
                    ? [targetServiceAccount]
                    : [],
                  finalList: (currentServiceAccounts ?? []).filter(
                    (r) => r.id !== targetServiceAccount?.id
                  ),
                });
              }}>
              <FormattedMessage
                id='IHGLSj'
                defaultMessage='Yes, Unlink'
                description='Yes, Unlink button for action'
              />
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={handleModalClose}
              isDisabled={isSASaving}>
              <FormattedMessage
                id='6PdOcy'
                defaultMessage='Cancel'
                description='Cancel'
              />
            </Button>
          }
        />
      );
    }

    if (action === A_KEY_OS) {
      return (
        <ManageOwnerAction
          serviceId={id}
          isModalOpen={isModalOpen}
          handleModalClose={handleModalClose}
          handleSubmit={handleSubmit}
          isLoading={isMOSaving}
          currentOwners={currentOwners ?? []}
        />
      );
    }

    batch(() => {
      dispatch(
        globalActions.setGlobalAlertBanner({
          ...ALERT_BANNER_DEFAULT,
          kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
          title: '',
          description: intl.formatMessage({
            id: '8kn8tR',
            defaultMessage: 'Failed to execute the action.',
            description: 'Failed to execute the action.',
          }),
          isOpen: true,
        })
      );
      dispatch(serviceIdentityActions.setActionManager(null));
    });

    return <div />;
  }, [
    intl,
    action,
    policyId,
    isModalOpen,
    isDActionLoading,
    handleModalClose,
    handleSubmit,
    isRotatingPassword,
    id,
    isSASaving,
    currentServiceAccounts,
    targetServiceAccount,
    currentOwners,
    isMOSaving,
    dispatch,
  ]);

  return isOpen && renderModals();
}

ServiceIdentityActionManager.defaultProps = {
  isDetailsPage: false,
  isBulkAction: false,
  mcKeys: undefined,
};

export default memo(ServiceIdentityActionManager);
